package com.tovtalk.data.remote

import io.ktor.client.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.cache.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.plugins.logging.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlinx.serialization.json.JsonPrimitive
import io.ktor.client.request.forms.*
import io.ktor.utils.io.core.*
import io.ktor.client.plugins.auth.*
import io.ktor.client.plugins.auth.providers.*
import com.tovtalk.data.local.TokenManager
import com.tovtalk.utils.logger.Logger
import com.tovtalk.data.remote.dto.LoginResponse
import io.ktor.client.call.body
import io.ktor.http.content.*
import io.ktor.client.statement.bodyAsText
import com.tovtalk.data.network.BandwidthEstimator
import com.tovtalk.data.network.BandwidthInterceptorPlugin

/**
 * API Client - Ktor HTTP Client
 */
class ApiClient(
    private val baseUrl: String,
    private val apiKey: String,
    private val getAuthToken: () -> String?,
    val bandwidthEstimator: BandwidthEstimator = BandwidthEstimator()
) {
    /**
     * Host of our API, derived from [baseUrl]. Used to scope the bearer token so
     * it is only attached to requests targeting our backend — never leaked to
     * third-party hosts (e.g. the ads host that shares this same httpClient).
     */
    private val apiHost: String = try {
        Url(baseUrl).host
    } catch (_: Exception) {
        ""
    }

    val httpClient = HttpClient {
        install(ContentNegotiation) {
            json(Json {
                ignoreUnknownKeys = true
                isLenient = true
            })
        }

        // Honors server Cache-Control / ETag headers. No-op for responses without
        // those headers, so it's safe to install unconditionally and becomes
        // effective once endpoints start emitting cache directives.
        install(HttpCache)

        install(Logging) {
            // SECURITY: route Ktor's request/response logs through our sanitizing
            // logger so the bearer token and Api-Key are never written to logcat /
            // browser console (LogLevel.INFO logs headers verbatim). We redact the
            // sensitive header values rather than disabling logging entirely, so
            // diagnostics survive while secrets do not leak.
            logger = object : io.ktor.client.plugins.logging.Logger {
                override fun log(message: String) {
                    com.tovtalk.utils.logger.Logger.d(sanitizeLog(message), "ApiClient")
                }
            }
            level = LogLevel.INFO
            // Belt-and-suspenders: Ktor's own header sanitizer also masks these
            // (covers any code path that bypasses our custom logger above).
            sanitizeHeader { header ->
                header.equals(HttpHeaders.Authorization, ignoreCase = true) ||
                    header.equals("Api-Key", ignoreCase = true)
            }
        }

        install(HttpTimeout) {
            requestTimeoutMillis = 30_000
            connectTimeoutMillis = 10_000
            socketTimeoutMillis = 30_000
        }

        install(BandwidthInterceptorPlugin) {
            estimator = bandwidthEstimator
        }

        install(Auth) {
            bearer {
                loadTokens {
                    // Read both tokens atomically so a concurrent refresh can't hand
                    // us a torn (new access / old refresh) pair.
                    val (accessToken, refreshToken) = TokenManager.tokenSnapshot()
                    if (accessToken != null && refreshToken != null) {
                        BearerTokens(accessToken, refreshToken)
                    } else if (accessToken != null) {
                        BearerTokens(accessToken, "")
                    } else {
                        null
                    }
                }

                // Only attach the bearer token to our own API host. Other hosts
                // sharing this client (e.g. the ads host, S3) must not receive it.
                sendWithoutRequest { request -> apiHost.isNotEmpty() && request.url.host == apiHost }

                refreshTokens {
                    val refreshToken = TokenManager.refreshToken ?: return@refreshTokens null

                    try {
                        val jsonBody = buildJsonObject {
                            put("refresh_token", JsonPrimitive(refreshToken))
                        }
                        val jsonString = Json.encodeToString(JsonObject.serializer(), jsonBody)
                        val response = client.post(ApiEndpoints.REFRESH_TOKEN) {
                            markAsRefreshTokenRequest()
                            contentType(ContentType.Application.Json)
                            setBody(io.ktor.http.content.TextContent(jsonString, ContentType.Application.Json))
                        }

                        val status = response.status
                        when {
                            status == HttpStatusCode.OK -> {
                                val result = response.body<LoginResponse>()
                                if (result.status && result.data != null) {
                                    TokenManager.accessToken = result.data.accessToken
                                    TokenManager.refreshToken = result.data.refreshToken
                                    BearerTokens(result.data.accessToken, result.data.refreshToken)
                                } else {
                                    // 200 OK but status==false / no data → the refresh token
                                    // is not usable. Treat as a non-recoverable session loss,
                                    // same as a 401, instead of leaving the session stuck.
                                    Logger.w("Refresh returned 200 but status=false/no data. Session expired.", "ApiClient")
                                    TokenManager.clearAndNotifyExpired()
                                    null
                                }
                            }
                            // 401 and other client-side rejections (400/403/422) mean the
                            // refresh token is invalid/expired/revoked and retrying won't help
                            // → clear tokens + notify so the NavHost redirects to onboarding.
                            status == HttpStatusCode.Unauthorized ||
                                status.value in 400..499 -> {
                                Logger.w("Refresh failed with $status (non-recoverable). Session expired.", "ApiClient")
                                TokenManager.clearAndNotifyExpired()
                                null
                            }
                            // 5xx / anything else is transient (server hiccup): keep tokens so a
                            // later request can retry the refresh.
                            else -> {
                                Logger.w("Refresh failed with $status (transient). Not clearing tokens.", "ApiClient")
                                null
                            }
                        }
                    } catch (e: Exception) {
                        Logger.e("refreshTokens failed: ${e.message}. Not clearing tokens.", "ApiClient", e)
                        null
                    }
                }
            }
        }

        defaultRequest {
            url(baseUrl)
            contentType(ContentType.Application.Json)
            header("Api-Key", apiKey)
        }
    }

    /**
     * Clears the cached bearer token in the Auth plugin so the next request
     * will call loadTokens again and pick up the current TokenManager values.
     * Call this after login/register when tokens have just been saved.
     */
    fun clearBearerTokenCache() {
        try {
            httpClient.authProviders.filterIsInstance<BearerAuthProvider>().firstOrNull()?.clearToken()
            Logger.d("Bearer token cache cleared", "ApiClient")
        } catch (e: Exception) {
            Logger.e("clearBearerTokenCache failed: ${e.message}", "ApiClient", e)
        }
    }

    /** Shared S3 upload client (no auth headers). Reused across uploads to avoid connection churn. */
    private val s3Client by lazy {
        HttpClient {
            install(HttpTimeout) {
                requestTimeoutMillis = 600_000
                connectTimeoutMillis = 30_000
                socketTimeoutMillis = 600_000
            }
        }
    }

    fun close() {
        httpClient.close()
        try { s3Client.close() } catch (_: Exception) { }
    }

    suspend fun getPresignedUploadUrl(
        filename: String,
        contentType: String
    ): io.ktor.client.statement.HttpResponse {
        return httpClient.post("${baseUrl}${ApiEndpoints.MEDIA_UPLOAD_INIT}") {
            setBody(com.tovtalk.data.remote.dto.MediaUploadInitRequest(filename, contentType))
        }
    }

    suspend fun uploadToS3(
        url: String,
        bytes: ByteArray,
        contentType: String,
        onProgress: ((Float) -> Unit)? = null
    ): Boolean {
        try {
            Logger.d("Starting S3 Upload", "ApiClient")
            val response = s3Client.put(url) {
                setBody(bytes)
                contentType(ContentType.parse(contentType))
                header("x-amz-acl", "private")
                onUpload { bytesSent, totalBytes ->
                    val total = totalBytes ?: return@onUpload
                    if (total > 0L) {
                        onProgress?.invoke(bytesSent.toFloat() / total.toFloat())
                    }
                }
            }
            Logger.d("S3 Upload Response: ${response.status}", "ApiClient")
            if (!response.status.isSuccess()) {
                Logger.w("S3 Error Body: ${response.bodyAsText()}", "ApiClient")
            }
            return response.status.isSuccess()
        } catch (e: Exception) {
            Logger.e("S3 Upload failed: ${e.message}", "ApiClient", e)
            e.printStackTrace()
            return false
        }
    }

    suspend fun confirmUpload(
        assetId: String
    ): io.ktor.client.statement.HttpResponse {
        return httpClient.post("${baseUrl}${ApiEndpoints.MEDIA_UPLOAD_COMPLETE}") {
           setBody(com.tovtalk.data.remote.dto.MediaUploadCompleteRequest(assetId))
        }
    }
}

/**
 * Redacts secret header values from a Ktor log line before it is written.
 *
 * The custom Ktor [io.ktor.client.plugins.logging.Logger] receives already-formatted
 * multi-line strings (e.g. "Authorization: Bearer eyJ..."), so the plugin's own
 * `sanitizeHeader` may not always apply to them. We mask anything following the
 * Authorization/Api-Key header name on its line so tokens never reach the device log.
 */
private fun sanitizeLog(message: String): String {
    val sensitive = listOf("Authorization", "Api-Key")
    return message.lineSequence().joinToString("\n") { line ->
        val header = sensitive.firstOrNull { line.trimStart().startsWith("$it:", ignoreCase = true) }
        if (header != null) {
            val prefix = line.substring(0, line.indexOf(':') + 1)
            "$prefix ***"
        } else {
            line
        }
    }
}

/**
 * API Endpoints
 */
object ApiEndpoints {
    // Base
    private const val API_VERSION = "v2"
    private const val API_BASE = "api/$API_VERSION/"

    // Auth
    const val LOGIN = "${API_BASE}auth/login"
    const val LOGIN_FIREBASE = "${API_BASE}auth/login/firebase"
    const val REGISTER = "${API_BASE}auth/register"
    const val LOGOUT = "${API_BASE}auth/logout"
    const val REFRESH_TOKEN = "${API_BASE}auth/refresh"
    const val GUEST_TOKEN = "${API_BASE}guest/token"
    const val GUEST_REGISTER = "${API_BASE}guest/register"
    const val SESSIONS = "${API_BASE}auth/sessions"
    const val VERIFICATION_START = "${API_BASE}auth/verification/start"
    const val VERIFICATION_STATUS = "${API_BASE}auth/verification/status"
    const val DELETE_ACCOUNT = "${API_BASE}auth/delete-account"

    // User
    const val USER_ME = "${API_BASE}users/me"
    const val USER_DETAILS = "${API_BASE}users/"
    const val USER_BY_USERNAME = "${API_BASE}users/by-username/"
    const val UPDATE_PROFILE = "${API_BASE}users/me"
    const val UPDATE_SETTINGS = "${API_BASE}users/me"

    // Device Token (FCM)
    const val DEVICE_TOKEN = "${API_BASE}users/me/device-token"
    /** Session restore: get Firebase custom token when app has API session but Firebase not signed in */
    const val FIREBASE_CUSTOM_TOKEN = "${API_BASE}users/me/firebase-custom-token"

    // Chat notifications (replace Firebase Cloud Functions – send FCM from our backend)
    const val CHAT_NOTIFY_MESSAGE_SENT = "${API_BASE}chat/notify-message-sent"
    const val CHAT_NOTIFY_CHAT_REQUEST = "${API_BASE}chat/notify-chat-request"

    // Follow
    const val FOLLOW = "${API_BASE}users/"  // + {user_id}/follow
    const val UNFOLLOW = "${API_BASE}users/"  // + {user_id}/follow (DELETE)
    const val FOLLOW_PREFERENCES = "${API_BASE}users/"  // + {user_id}/follow-preferences

    // Block
    const val BLOCK = "${API_BASE}users/"  // + {user_id}/block
    const val BLOCKED_USERS = "${API_BASE}users/blocked"

    // Posts
    const val CREATE_POST = "${API_BASE}posts"
    const val DELETE_POST = "${API_BASE}posts/"

    // Feed (unified v2 endpoint — replaces DISCOVER_POSTS, FOLLOWING_POSTS, NEARBY_POSTS, USER_POSTS, SAVED_POSTS)
    fun feed(mode: String, cursor: String? = null, limit: Int = 20, userId: Int? = null): String {
        val params = mutableListOf("mode=$mode", "limit=$limit")
        cursor?.let { params.add("cursor=$it") }
        userId?.let { params.add("user_id=$it") }
        return "${API_BASE}feed?${params.joinToString("&")}"
    }

    // Post action functions
    fun post(ref: String) = "${API_BASE}posts/$ref"
    fun postLike(postId: Int) = "${API_BASE}posts/$postId/like"
    fun postUnlike(postId: Int) = "${API_BASE}posts/$postId/unlike"
    fun postSave(postId: Int) = "${API_BASE}posts/$postId/save"
    fun postUnsave(postId: Int) = "${API_BASE}posts/$postId/unsave"
    fun postView(postId: Int) = "${API_BASE}posts/$postId/view"
    fun userStories(userId: Int) = "${API_BASE}posts/user/$userId/stories"

    // Comments
    const val POST_COMMENTS = "${API_BASE}comments/post/"
    const val ADD_COMMENT = "${API_BASE}comments/post/"
    const val DELETE_COMMENT = "${API_BASE}comments/"
    const val LIKE_COMMENT = "${API_BASE}comments/"  // + {comment_id}/like
    const val UNLIKE_COMMENT = "${API_BASE}comments/"  // + {comment_id}/like (DELETE)
    const val COMMENT_REPLIES = "${API_BASE}comments/"  // + {comment_id}/replies

    // Notifications
    const val NOTIFICATIONS_LIST = "${API_BASE}notifications"  // unified feed (activity + admin)
    const val ACTIVITY_NOTIFICATIONS = "${API_BASE}notifications/activity"
    const val DELETE_ACTIVITY_NOTIFICATION = "${API_BASE}notifications/activity/"
    const val MARK_READ = "${API_BASE}notifications/"  // + {notification_id}/read (activity)
    const val MARK_ADMIN_READ = "${API_BASE}notifications/admin/"  // + {received_id}/read
    const val MARK_ALL_READ = "${API_BASE}notifications/read-all"

    // Settings
    const val KOSHER_LEVELS = "${API_BASE}app/kosher-levels"
    /** Version requirements and store URLs – no auth. */
    const val APP_CONFIG = "${API_BASE}app/config"
    /** Support contacts (name, phone) for app "save to contacts". */
    const val CONTACT_PHONES = "${API_BASE}app/contact-phones"
    /** Content categories for onboarding and settings. */
    const val CATEGORIES = "${API_BASE}app/categories"

    // Media upload
    const val MEDIA_UPLOAD_INIT = "${API_BASE}media/upload/init"
    const val MEDIA_UPLOAD_COMPLETE = "${API_BASE}media/upload/complete"
    fun mediaStatus(assetId: String) = "${API_BASE}media/$assetId/status"

    // Events
    const val EVENTS_BATCH = "${API_BASE}events/batch"
    const val RICH_EVENTS_V2 = "${API_BASE}events"  // Rich events pipeline (video_session + interaction)


    // Search
    const val SEARCH_USERS = "${API_BASE}users/search"
    const val SUGGESTED_USERS = "${API_BASE}users/suggested"

    // Report
    const val REPORT_REASONS = "${API_BASE}reports/reasons"
    const val REPORT_POST = "${API_BASE}reports/post"
    const val REPORT_USER = "${API_BASE}reports/user"

    /** Info pages (about, privacy, terms, account-deletion, safety). Returns JSON { content, locale }. */
    const val PAGES = "${API_BASE}pages/"
    /** List of all admin-managed page slugs. Returns JSON { slugs: [..] }. */
    const val DOC_SLUGS = "${API_BASE}pages/doc-slugs"

    // Hashtags
    const val HASHTAG_POSTS = "${API_BASE}hashtags"  // /{tag}/posts
    const val HASHTAG_TRENDING = "${API_BASE}hashtags/trending"
    const val HASHTAG_SEARCH = "${API_BASE}hashtags/search"
}
